import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { wipInventory, type WipInventoryListItem } from '@/api/wipInventory';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'production.wip_inventory.manage';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Чернетка',
  posted: '✓ Проведено',
  cancelled: '⊘ Скасовано',
};

export default function WipInventoryPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['wip-inventory', orgId],
    queryFn: () => wipInventory.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<WipInventoryListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата документа',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'inventory_date',
        header: 'Дата інвентаризації',
        width: 180,
        sortValue: (r) => r.inventory_date ?? '',
        cell: (r) =>
          r.inventory_date ? new Date(r.inventory_date).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'warehouse_id',
        header: 'Склад',
        sortValue: (r) => r.warehouse_id ?? '',
        cell: (r) => r.warehouse_id,
      },
      {
        key: 'status',
        header: 'Статус',
        width: 150,
        sortValue: (r) => r.status ?? '',
        cell: (r) => STATUS_LABEL[r.status] ?? r.status,
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати Інвентаризацію НЗВ у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Інвентаризація НЗВ</h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/wip-inventory/new')}
        >
          + Нова інвентаризація
        </button>
      </div>
      <DataTable<WipInventoryListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/wip-inventory/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
